import { useQuery } from '@tanstack/react-query';
import { calendarApi, commonApi, counselorApi, cravingApi } from 'src/services';

/** 쿼리 키 상수 */
export const HOME_QUERY_KEYS = {
  all: ['home'] as const,
  main: () => [...HOME_QUERY_KEYS.all, 'main'] as const,
  todayDiaryStatus: () => [...HOME_QUERY_KEYS.all, 'todayDiaryStatus'] as const,
  dailyCravingHistory: (days: number) =>
    [...HOME_QUERY_KEYS.all, 'dailyCravingHistory', days] as const,
  executionAnalysis: () => [...HOME_QUERY_KEYS.all, 'executionAnalysis'] as const,
} as const;

/** 홈 메인 데이터 조회 */
export const useHomeMain = () => {
  return useQuery({
    queryKey: HOME_QUERY_KEYS.main(),
    queryFn: async () => {
      const result = await commonApi.getMain();
      if (result?.resultCode === 'S' && result.data) {
        return result.data;
      }
      throw new Error(result?.errorMessage || '홈 메인 데이터 조회 실패');
    },
    staleTime: 5 * 60 * 1000,
  });
};

/** 오늘 일지 작성 여부 조회 */
export const useTodayDiaryStatus = () => {
  return useQuery({
    queryKey: HOME_QUERY_KEYS.todayDiaryStatus(),
    queryFn: async () => {
      const result = await calendarApi.getTodayDiaryStatus();
      if (result?.resultCode === 'S' && result.data) {
        return result.data;
      }
      throw new Error(result?.errorMessage || '오늘 일지 상태 조회 실패');
    },
    staleTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
  });
};

/** 일별 갈망 히스토리 조회 */
export const useDailyCravingHistory = (days = 14) => {
  return useQuery({
    queryKey: HOME_QUERY_KEYS.dailyCravingHistory(days),
    queryFn: async () => {
      const result = await cravingApi.getDailyCravingHistory(days);
      if (result?.resultCode === 'S' && result.data) {
        return result.data;
      }
      throw new Error(result?.errorMessage || '일별 갈망 히스토리 조회 실패');
    },
    staleTime: 5 * 60 * 1000,
  });
};

/** 분석 실행 (councelor 에이전트: 이상 분석, 행동 패턴, 감정 분석 등) */
export const useExecutionAnalysis = (enabled = true) => {
  return useQuery({
    queryKey: HOME_QUERY_KEYS.executionAnalysis(),
    queryFn: async () => {
      // userId만 전달하고 data는 비어있음 (서버가 DB에서 데이터 조회하여 분석)
      const result = await counselorApi.executeResult({
        data: [],
      });
      if (result?.statusCode === '201' && result.data) {
        return result.data;
      }
      throw new Error('분석 실행 실패');
    },
    staleTime: 10 * 60 * 1000, // 10분
    enabled, // 선택적으로 쿼리 활성화 제어
    refetchOnMount: 'always',
  });
};
